import type { KeyboardEvent, MouseEvent, ReactNode } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

/** One row style for every panel of the mail sidebar (Outlook Folders,
 *  Triage, Daily Summary, Custom Folders, AI Classifications): the same
 *  height, type size, icon slot, indentation, selection and count badge,
 *  so a folder looks like a folder wherever it is listed. Modeled on the
 *  Outlook folder tree, made roomier on 2026-08-03. */

const ROW_STATE = {
  // Plainly the current one (2026-10-02: it was easy to miss): a filled
  // accent band with a strong edge.
  selected: "border-sky-500 bg-sky-100 text-sky-900 shadow-sm ring-1 ring-inset ring-sky-300/70 font-semibold dark:border-sky-400 dark:bg-sky-500/25 dark:text-white dark:ring-sky-400/30",
  open: "border-sky-400/80 dark:border-sky-500/70 bg-slate-50/70 dark:bg-slate-800/30 text-slate-800 dark:text-slate-100",
  muted: "border-transparent text-slate-500/80 dark:text-slate-400/70 hover:bg-slate-100/70 dark:hover:bg-slate-800/40",
  rest: "border-transparent text-slate-700 dark:text-slate-200 hover:bg-slate-100/70 dark:hover:bg-slate-800/40",
} as const;

export function NavRow({
  label,
  icon,
  depth = 0,
  expandable,
  open = false,
  onToggle,
  selected = false,
  muted = false,
  strong = false,
  onClick,
  onContextMenu,
  badge,
  trailing,
  title,
}: {
  label: ReactNode;
  /** A 14 px glyph (or a chip of about that size), tinted with the row. */
  icon?: ReactNode;
  /** Nesting level in a tree; indents the row. */
  depth?: number;
  /** Tree rows: true shows a chevron, false keeps its column empty so the
   *  icons line up. Leave unset in a flat list (no chevron column). */
  expandable?: boolean;
  open?: boolean;
  onToggle?: () => void;
  selected?: boolean;
  /** A sibling's branch is open: this row steps back. */
  muted?: boolean;
  /** A label set in bold by the user (a custom folder's isBold). */
  strong?: boolean;
  onClick?: () => void;
  onContextMenu?: (e: MouseEvent<HTMLDivElement>) => void;
  /** Usually a NavCount, at the right edge. */
  badge?: ReactNode;
  /** After the badge, e.g. an edit button shown on hover. */
  trailing?: ReactNode;
  title?: string;
}) {
  const state = selected ? "selected" : expandable && open ? "open" : muted ? "muted" : "rest";
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick?.();
    } else if (expandable && (e.key === "ArrowRight" || e.key === "ArrowLeft")) {
      if ((e.key === "ArrowRight") !== open) {
        e.preventDefault();
        onToggle?.();
      }
    }
  };
  return (
    <div
      role="button"
      tabIndex={0}
      title={title}
      aria-current={selected || undefined}
      aria-expanded={expandable ? open : undefined}
      data-nav-row=""
      onClick={onClick}
      onKeyDown={onKeyDown}
      onContextMenu={onContextMenu}
      className={
        "group flex items-center gap-2 pr-2 text-[14px] border-l-[3px] rounded-r-md cursor-pointer outline-none transition-colors duration-150 " +
        "focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sky-500/60 " +
        ROW_STATE[state] +
        (strong && !selected ? " font-semibold" : "")
      }
      style={{ paddingLeft: `${0.5 + depth * 0.75}rem`, minHeight: 34 }}
    >
      {expandable === undefined ? null : expandable ? (
        <button
          type="button"
          tabIndex={-1}
          aria-label={open ? "Collapse" : "Expand"}
          onClick={(e) => {
            e.stopPropagation();
            onToggle?.();
          }}
          className="flex h-4 w-4 flex-shrink-0 items-center justify-center text-slate-400"
        >
          {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>
      ) : (
        <span className="w-4 flex-shrink-0" />
      )}
      {icon != null && (
        <span
          aria-hidden
          className={
            "flex w-[18px] flex-shrink-0 items-center justify-center " +
            (selected ? "text-sky-600 dark:text-sky-300" : "text-slate-400 dark:text-slate-500")
          }
        >
          {icon}
        </span>
      )}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {badge}
      {trailing}
    </div>
  );
}

/** The indented, dashed-guide container for a branch's children. */
export function NavChildren({ children }: { children: ReactNode }) {
  return <div className="ml-2 pl-1 border-l border-dashed border-slate-300/70 dark:border-slate-700/60">{children}</div>;
}

/** The sidebar's one count badge. With `unread`, shows "unread/total" with
 *  the unread part in the accent; `emphasis` puts a plain count in the
 *  accent (counts that ask for action). `updating` dims a last-known value
 *  while a fresh one is on its way. */
export function NavCount({
  value,
  unread,
  emphasis = false,
  updating = false,
  title,
}: {
  value: number;
  unread?: number;
  emphasis?: boolean;
  updating?: boolean;
  title?: string;
}) {
  const accent = "font-bold text-sky-700 dark:text-sky-300";
  return (
    <span
      title={[title, updating ? "updating" : ""].filter(Boolean).join(" · ") || undefined}
      className={
        "ml-1.5 flex-shrink-0 rounded-full bg-slate-200/70 px-1.5 min-w-[22px] text-center text-[10px] font-semibold leading-[18px] tabular-nums text-slate-600 dark:bg-slate-700/50 dark:text-slate-300" +
        (updating ? " opacity-60" : "")
      }
    >
      {unread !== undefined && unread > 0 ? (
        <>
          <span className={accent}>{unread.toLocaleString()}</span>
          <span className="mx-px opacity-50">/</span>
          <span>{value.toLocaleString()}</span>
        </>
      ) : (
        <span className={emphasis ? accent : undefined}>{value.toLocaleString()}</span>
      )}
    </span>
  );
}

/** Stands in for a NavCount while the count loads, at the same size. */
export function NavCountPulse() {
  return (
    <span
      aria-hidden
      className="ml-1.5 inline-block h-[18px] w-[26px] flex-shrink-0 rounded-full bg-slate-200/80 dark:bg-slate-700/60 animate-pulse motion-reduce:animate-none"
    />
  );
}
